/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ElementRef, ChangeDetectionStrategy, Input, Output, EventEmitter,
  ViewChild, Inject, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import { RANDOM_ID } from '@app/constants/general.constants';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { Airport } from '@app/modules/core/interfaces/airport';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { TOGGLE_SEARCH_PAGE_DELAY } from '@shared/base-search-bar/constants/search.constants';
import { TooltipOrientation } from '@shared/tooltip/entities/tooltip-orientation';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { EventProperties } from '@avo/interface/trigger-props';
import { locationNotFoundAmplitudePropsMapper } from '@app/modules/core/mappers/amplitude-mappers';
import { BaseSearchInputAbstractComponent } from '@shared/base-search-bar/abstract/base-search-input.abstract';
import { LoaderRingType } from '@design-system/components/atoms/loader-ring/entities/loader-ring.types';
import { DropDownViewCloseAction } from '@ux/components/drop-down-view/entities/drop-down-view.types';

@Component({
  selector: 'app-search-input-all-places',
  templateUrl: './search-input-all-places.component.html',
  styleUrls: ['./search-input-all-places.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchInputAllPlacesComponent extends BaseSearchInputAbstractComponent implements OnInit, OnDestroy {
  @Input() searchType: SearchType;
  @Input() place: AutocompletePlace;
  @Input() placeType: PlaceType;
  @Input() countryCode: string;
  @Input() countryName: string;
  @Input() airports: Airport[] = [];
  @Input() delay: number = 500;
  @Input() eventPath: EventTarget[];
  @Input() isMobile: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() selected: boolean;
  @Input() isLoadingAutocompletePlaces: boolean = false;
  @Input() activateTooltip: boolean = false;
  @Input() activateCursor: boolean = false;
  @Input() arrivalPlace: AutocompletePlace;
  @Input() departurePlace: AutocompletePlace;
  @Input() set autoCompletePlaces(autoCompletePlaces: AutocompletePlaces) {
    this._autoCompletePlaces = autoCompletePlaces;
    setTimeout(() => this.sendNotFoundEvent(), TOGGLE_SEARCH_PAGE_DELAY);
  }

  @Output() selectPlace: EventEmitter<SelectAutocompletePrediction> = new EventEmitter<SelectAutocompletePrediction>();
  @Output() locationNotFoundEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() popularSelected: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();

  @ViewChild('destination', { static: true }) public destination: ElementRef;

  public id: number = Math.floor(Math.random() * RANDOM_ID);
  public tooltipOrientation: string = TooltipOrientation.LEFT;

  private _autoCompletePlaces: AutocompletePlaces;

  public readonly loaderRingType = LoaderRingType.Neutral;
  public readonly closeAction = DropDownViewCloseAction.Close;

  constructor(
    protected cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!isEmpty(this.place) && this.place.description) {
        this.countryName = (this.place.code) ? `${this.place.description}, ${this.place.country}` : `${this.place.description}`;
        this.countryCode = (this.place.code) ? this.place.code : this.place.related_city;
      }
    }
  }

  ngOnDestroy(): void {
    this.removeAutoCompletePlaces.emit();
  }

  get tooltipMessage(): string {
    return this.placeType === PlaceType.ARRIVAL ? 'SEARCH_MODULE.PLACE_TOOLTIP.ARRIVAL' : 'SEARCH_MODULE.PLACE_TOOLTIP.DEPARTURE';
  }
  get getCountryName(): string {
    let countryName = this.defaultCountry || this.countryName;
    if (!isEmpty(this.place) && this.place.description) {
      countryName = (this.place.code) ? `${this.place.description}, ${this.place.country}` : `${this.place.description}`;
    }
    return countryName;
  }
  get getCountryCode(): string {
    let countryCode = this.defaultCityCode || this.countryCode;
    if (!isEmpty(this.place) && this.place.related_city) {
      countryCode = this.place.related_city;
    }
    if (!isEmpty(this.place) && this.place.code) {
      countryCode = this.place.code;
    }
    return countryCode;
  }

  public selectAirportOrCity(data: { place: AutocompletePlace, isAirport: boolean }): void {
    this.emitSelectPlace(data?.place, data?.isAirport)
  }

  get isArrival(): boolean {
    return this.placeType === PlaceType.ARRIVAL;
  }
  get showCursorHome(): boolean {
    const validCondition = this.searchType === SearchType.HOME_FLIGHT
      || this.searchType === SearchType.HOME_HOTELS || this.searchType === SearchType.HOME_BUSES;
    return validCondition && this.activateCursor && !this.hasPlaceSelected;
  }
  get isResultPageBus(): boolean {
    return this.isResultsPage && this.searchBuses;
  }

  get hasPlaceSelected(): boolean {
    return this.place?.code ? true : false;
  }
  get defaultCityCode(): string {
    return this.place ? this.searchBuses ? this.defaultBusesCityCode : this.place.code : null;
  }
  get autoCompletePlaces(): AutocompletePlaces {
    return this._autoCompletePlaces;
  }
  get defaultBusesCityCode(): string {
    return this.place.description ? `${this.place.description}${this.getStateCode(this.place.statecode)}` : null;
  }
  get defaultCountry(): string {
    if (!isEmpty(this.place)) {
      if (this.place.country) {
        if (this.searchBuses) {
          return this.place.country;
        } else {
          return this.place.description.includes(this.place.country) ?
            `${this.place.description}` : `${this.place.description}, ${this.place.country}`;
        }
      } else {
        return this.place.description;
      }
    } else {
      return null;
    }
  }
  get defaultCityName(): string {
    return (this.place ? this.searchBuses ? this.place.description : this.place.code : null);
  }
  get placeTypeText(): string {
    return this.placeType === PlaceType.DEPARTURE ? 'AUTOS_ORIGIN' : 'AUTOS_DESTINATION';
  }
  get placeholderText(): string {
    return this.placeType === PlaceType.DEPARTURE ? 'FROM' : 'TO';
  }
  get resultsPlaces(): AutocompletePlaces {
    return {
      airports: this.autoCompletePlaces?.airports,
      cities: this.autoCompletePlaces?.cities
    };
  }
  get isResultsPage(): boolean {
    return !this.isMobile ? this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS : false;
  }
  get isResultsPageMobile(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS
      || this.searchType === SearchType.AUTOS_RESULTS;
  }
  get searchBuses(): boolean {
    return this.searchType === SearchType.HOME_BUSES || this.searchType === SearchType.BUSES_RESULTS;
  }
  get sendLocationNotFoundEvent(): boolean {
    return this.showInput && !!this.value && this.autoCompletePlaces?.cities?.length === 0;
  }
  get destinationInputId(): string {
    return `destination-search-input-place-${this.placeType}`;
  }
  public showAirports(airports: AutocompletePlace[]): boolean {
    return !!airports && airports.length > 0;
  }
  public oneAirports(airports: AutocompletePlace[]): boolean {
    return !!airports && airports.length === 1;
  }
  public showAllAirports(airports: AutocompletePlace[]): boolean {
    return (this.showAirports(airports) && !this.oneAirports(airports)) || !this.showAirports(airports);
  }
  public moreThanOneAirport(airports: AutocompletePlace[]): boolean {
    return !!airports && airports.length > 1;
  }
  public showAirportsByCity(airports: AutocompletePlace[]): boolean {
    return !this.searchBuses && this.moreThanOneAirport(airports);
  }

  public emitSelectPlace(place: AutocompletePlace, isAirport: boolean = false): void {
    this.selectPlace.emit({ ...place, is_airport: isAirport, type: this.placeType });
    this.toggleInput();
  }

  private getStateCode(statecode: string): string {
    if (this.searchBuses && statecode) {
      const splitStateCode = statecode.split('_')[1];
      return splitStateCode ? ` (${splitStateCode})` : ` (${statecode})`;
    } else return '';
  }
  public cityLabel(place: AutocompletePlace, searchBuses: boolean): string {
    const description = !isEmpty(place.highlight_description) ? place.highlight_description : place.description;
    return !searchBuses ? place.state ? `${description}, ${place.state}, ${place.country}` : `${description}, ${place.country}` :
      `${description}${this.getStateCode(place.statecode)}, ${place.country}`;
  }
  private sendNotFoundEvent(): void {
    if (this.sendLocationNotFoundEvent) {
      this.locationNotFoundEvent.emit(
        locationNotFoundAmplitudePropsMapper(this.searchType, this.value, this.destinationInputId));
    }
  }
  public trackById(index: number): number {
    return index;
  }
}
